import { loadStripe } from "@stripe/stripe-js";

import { useEffect, useMemo, useState } from "react";
import CartValidationAlert from "../../components/cart/CartValidationAlert";
import FulfillmentSelector, { getFulfillmentOptions } from "../../components/cart/FulfillmentSelector";
import ManualPaymentInstructions from "../../components/payments/ManualPaymentInstructions";
import PaymentMethodSelector from "../../components/payments/PaymentMethodSelector";
import StripePaymentForm from "../../components/payments/StripePaymentForm";
import { useLocationSelection } from "../../context/LocationContext";
import { buildCartPayload, useCartValidation } from "../../hooks/useCartValidation";
import { useLocationPaymentOptions } from "../../hooks/useLocationPaymentOptions";
import {
  createCheckoutOrder,
  createStripePaymentIntent,
  fetchOrderStatus,
} from "../../services/publicApi";
import CartSummary from "../Cart/CartSummary";
import "./CheckoutPage.css";

const { Elements } = require("@stripe/react-stripe-js");

const initialCustomer = {
  customer_first_name: "",
  customer_last_name: "",
  customer_email: "",
  customer_phone: "",
  requested_date: "",
  requested_time: "",
  delivery_address_line1: "",
  delivery_address_line2: "",
  delivery_city: "",
  delivery_state: "",
  delivery_postal_code: "",
  special_instructions: "",
  payment_reference: "",
  customer_payment_note: "",
};

function CheckoutPage({ onChooseLocation }) {
  const {
    cartItems,
    clearCart,
    fulfillmentType,
    selectedLocation,
    setFulfillmentType,
  } = useLocationSelection();
  const [customer, setCustomer] = useState(initialCustomer);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [order, setOrder] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState("");
  const [reviewedTotalSignature, setReviewedTotalSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { paymentOptions, loading: paymentOptionsLoading } = useLocationPaymentOptions(selectedLocation?.slug);
  const { cartResult, error: cartError, loading: cartLoading, runValidation } = useCartValidation({
    cartItems,
    fulfillmentType,
    selectedLocation,
  });
  const fulfillmentOptions = getFulfillmentOptions(selectedLocation);
  const checkoutDisabled =
    submitting ||
    paymentOptionsLoading ||
    cartLoading ||
    !fulfillmentOptions.length ||
    !cartResult?.valid;

  const stripePromise = useMemo(() => {
    const publishableKey = paymentOptions?.stripe?.publishable_key;
    return publishableKey ? loadStripe(publishableKey) : null;
  }, [paymentOptions?.stripe?.publishable_key]);

  useEffect(() => {
    setReviewedTotalSignature("");
  }, [cartItems, fulfillmentType, selectedLocation]);

  useEffect(() => {
    const methods = paymentOptions?.payment_methods || {};

    if (!methods[paymentMethod]?.enabled) {
      const firstEnabledMethod = Object.entries(methods).find(([, config]) => config.enabled)?.[0];
      setPaymentMethod(firstEnabledMethod || "stripe");
    }
  }, [paymentMethod, paymentOptions]);

  function updateCustomer(field, value) {
    setCustomer((currentCustomer) => ({ ...currentCustomer, [field]: value }));
  }

  async function submitOrder(event) {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const validation = await runValidation();

      if (!validation?.valid) {
        setError("Please review your cart before placing the order.");
        return;
      }

      const totalSignature = `${validation.subtotal}|${validation.tax_amount}|${validation.delivery_fee}|${validation.service_fee}|${validation.total}`;
      const previousTotalSignature = cartResult
        ? `${cartResult.subtotal}|${cartResult.tax_amount}|${cartResult.delivery_fee}|${cartResult.service_fee}|${cartResult.total}`
        : totalSignature;
      const totalsChanged = totalSignature !== previousTotalSignature;
      const hasPriceWarnings = Boolean(validation.warnings?.length);
      const needsReview = (totalsChanged || hasPriceWarnings) && reviewedTotalSignature !== totalSignature;

      if (needsReview) {
        setReviewedTotalSignature(totalSignature);
        setError("Your order total changed. Please review the updated fees.");
        return;
      }

      const orderPayload = {
        ...buildCartPayload({ cartItems, fulfillmentType, selectedLocation }),
        ...customer,
        payment_method: paymentMethod,
        idempotency_key: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      };
      const orderResponse = await createCheckoutOrder(orderPayload);
      setOrder(orderResponse.order);

      if (orderResponse.next_action === "create_payment_intent") {
        const intent = await createStripePaymentIntent({
          order_number: orderResponse.order.order_number,
          customer_email: orderResponse.order.customer_email,
        });
        setClientSecret(intent.client_secret);
      } else {
        clearCart();
        window.location.href = `/order/${orderResponse.order.order_number}/confirmation?email=${encodeURIComponent(
          orderResponse.order.customer_email,
        )}`;
      }
    } catch (requestError) {
      setError(requestError.message || "We could not place your order.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStripePaid() {
    const latestOrder = await fetchOrderStatus(order.order_number, order.customer_email).catch(() => order);
    setOrder(latestOrder);
    clearCart();
    window.location.href = `/order/${order.order_number}/confirmation?email=${encodeURIComponent(order.customer_email)}`;
  }

  if (!cartItems.length && !order) {
    return (
      <main className="section checkout-page">
        <div className="container empty-state">
          <strong>Your bag is empty.</strong>
          <span>Add items before checkout.</span>
          <button className="btn btn-primary" onClick={onChooseLocation} type="button">
            Start an Order
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="section checkout-page">
      <div className="container checkout-layout">
        <section className="checkout-panel">
          <p className="eyebrow">Checkout</p>
          <h1>Place your order</h1>
          <CartValidationAlert cartResult={cartResult} error={cartError || error} />

          {!order ? (
            <form className="checkout-form" onSubmit={submitOrder}>
              <FulfillmentSelector
                fulfillmentType={fulfillmentType}
                location={selectedLocation}
                onChange={setFulfillmentType}
              />

              <div className="checkout-grid">
                <label>
                  <span>First name</span>
                  <input
                    required
                    value={customer.customer_first_name}
                    onChange={(event) => updateCustomer("customer_first_name", event.target.value)}
                  />
                </label>
                <label>
                  <span>Last name</span>
                  <input
                    required
                    value={customer.customer_last_name}
                    onChange={(event) => updateCustomer("customer_last_name", event.target.value)}
                  />
                </label>
                <label>
                  <span>Email</span>
                  <input
                    required
                    type="email"
                    value={customer.customer_email}
                    onChange={(event) => updateCustomer("customer_email", event.target.value)}
                  />
                </label>
                <label>
                  <span>Phone</span>
                  <input
                    required
                    value={customer.customer_phone}
                    onChange={(event) => updateCustomer("customer_phone", event.target.value)}
                  />
                </label>
                <label>
                  <span>Requested date</span>
                  <input
                    required
                    type="date"
                    value={customer.requested_date}
                    onChange={(event) => updateCustomer("requested_date", event.target.value)}
                  />
                </label>
                <label>
                  <span>Requested time</span>
                  <input
                    required
                    type="time"
                    value={customer.requested_time}
                    onChange={(event) => updateCustomer("requested_time", event.target.value)}
                  />
                </label>
              </div>

              {fulfillmentType === "delivery" ? (
                <section className="checkout-address-section">
                  <h2>Delivery address</h2>
                  <div className="checkout-grid">
                    <label>
                      <span>Address line 1</span>
                      <input
                        required
                        value={customer.delivery_address_line1}
                        onChange={(event) => updateCustomer("delivery_address_line1", event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Address line 2</span>
                      <input
                        value={customer.delivery_address_line2}
                        onChange={(event) => updateCustomer("delivery_address_line2", event.target.value)}
                      />
                    </label>
                    <label>
                      <span>City</span>
                      <input
                        required
                        value={customer.delivery_city}
                        onChange={(event) => updateCustomer("delivery_city", event.target.value)}
                      />
                    </label>
                    <label>
                      <span>State</span>
                      <input
                        required
                        value={customer.delivery_state}
                        onChange={(event) => updateCustomer("delivery_state", event.target.value)}
                      />
                    </label>
                    <label>
                      <span>ZIP code</span>
                      <input
                        required
                        value={customer.delivery_postal_code}
                        onChange={(event) => updateCustomer("delivery_postal_code", event.target.value)}
                      />
                    </label>
                  </div>
                </section>
              ) : null}

              <label className="checkout-full-field">
                <span>Special instructions</span>
                <textarea
                  value={customer.special_instructions}
                  onChange={(event) => updateCustomer("special_instructions", event.target.value)}
                />
              </label>

              <PaymentMethodSelector
                paymentOptions={paymentOptions}
                selectedMethod={paymentMethod}
                onChange={setPaymentMethod}
              />

              {paymentMethod !== "stripe" ? (
                <label className="checkout-full-field">
                  <span>Payment reference or note</span>
                  <textarea
                    placeholder="Optional confirmation number, sender name, or note"
                    value={customer.customer_payment_note}
                    onChange={(event) => updateCustomer("customer_payment_note", event.target.value)}
                  />
                </label>
              ) : null}

              <button className="btn btn-primary btn-block" disabled={checkoutDisabled} type="submit">
                {submitting ? "Placing Order" : "Place Order"}
              </button>
            </form>
          ) : null}

          {order && order.payment_method !== "stripe" ? <ManualPaymentInstructions order={order} /> : null}

          {order && clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripePaymentForm order={order} onPaid={handleStripePaid} />
            </Elements>
          ) : null}
        </section>

        <CartSummary cartResult={cartResult} disabled actionLabel="Order Total" loading={cartLoading} />
      </div>
    </main>
  );
}

export default CheckoutPage;
